(function () {
  'use strict';

  const LOG = '[kg-cart-poll]';
  const Kg = window.KgPricing;
  if (!Kg) {
    console.warn(`${LOG} kg-pricing-core.js must load first`);
    return;
  }

  const POLL_MS = 5 * 60 * 1000;
  const DRIFT_THRESHOLD_EGP = 1;
  const SWAP_CONCURRENCY = 1;

  let pollTimer = null;
  let inFlight = false;
  let activeSwaps = 0;
  let lastSwapAt = 0;
  const SWAP_MIN_INTERVAL_MS = 1500;

  function isCheckoutPage() {
    return /\/checkouts?(\/|$|\?)/.test(window.location.pathname);
  }

  function isProductPage() {
    return document.body && document.body.classList.contains('template-product');
  }

  function isCartPage() {
    return document.body && (
      document.body.classList.contains('template-cart') ||
      document.body.classList.contains('page-cart')
    );
  }

  function shouldRun() {
    if (isCheckoutPage()) return false;
    if (document.hidden) return false;
    return isCartPage() || isProductPage();
  }

  function readLineData(lineEl) {
    if (!lineEl) return null;
    return {
      sku: (lineEl.dataset.kgSku || '').trim(),
      weight: Kg.parseWeight(lineEl.dataset.kgWeight),
      prc: Kg.parseNum(lineEl.dataset.kgPrc),
      prcus: Kg.parseNum(lineEl.dataset.kgPrcus),
      quantity: Kg.parseNum(lineEl.dataset.kgQuantity) || 1,
      variantId: Number(lineEl.dataset.kgVariantId) || 0,
      lineId: lineEl.dataset.kgLineId ? Number(lineEl.dataset.kgLineId) || 0 : 0,
    };
  }

  function collectCartLines() {
    const out = [];
    const els = document.querySelectorAll('[data-kg-live-price]');
    for (const el of els) {
      if (el.tagName && el.tagName.toLowerCase() === 'kg-live-price') continue;
      const d = readLineData(el);
      if (d && d.sku) out.push({ el, data: d });
    }
    return out;
  }

  function computeLinePrice(data, gold) {
    const unit = Kg.computePrice({
      pr18: gold.pr18,
      usdRate: gold.dollar,
      weight: data.weight,
      prc: data.prc,
      prcus: data.prcus,
    });
    if (unit == null) return null;
    const rounded = Kg.roundToNearest5(unit);
    const qty = data.quantity > 0 ? data.quantity : 1;
    return { unit: rounded, line: rounded * qty };
  }

  function overrideLineDisplay(lineEl, linePrice) {
    if (!lineEl || !linePrice) return;
    lineEl.querySelectorAll('.price-item--regular, .product-option.price-original, .price.price--end').forEach((n) => {
      n.textContent = Kg.formatPrice(linePrice);
    });
    const totalEl = lineEl.querySelector('[data-kg-line-total]');
    if (totalEl) totalEl.textContent = Kg.formatPrice(linePrice);
  }

  async function fetchCart() {
    try {
      const res = await fetch(`${window.shopUrl || ''}/cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  function dispatchCartRefresh() {
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) { /* swallow */ }
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kg-cart-poll' });
      }
    } catch (e) { /* swallow */ }
  }

  async function swapLinePrice(line, computedUnit) {
    if (activeSwaps >= SWAP_CONCURRENCY) return { swapped: false, reason: 'busy' };
    const now = Date.now();
    if (now - lastSwapAt < SWAP_MIN_INTERVAL_MS) return { swapped: false, reason: 'cooldown' };
    if (!line || !line.sku) return { swapped: false, reason: 'no-sku' };

    activeSwaps += 1;
    lastSwapAt = now;
    try {
      const sync = await Kg.syncVariantPrice(line.sku);
      if (!sync.ok) return { swapped: false, reason: sync.reason || 'sync-failed' };

      const changeUrl = window.routes && window.routes.cart_change_url;
      const addUrl = window.routes && window.routes.cart_add_url;
      if (!changeUrl || !addUrl) return { swapped: false, reason: 'no-routes' };

      const changeRes = await fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: line.lineId, quantity: 0 }),
      });
      if (!changeRes.ok) return { swapped: false, reason: 'remove-failed' };

      const addBody = { id: line.variantId, quantity: Math.max(1, Math.round(line.quantity || 1)) };
      if (line.properties && Object.keys(line.properties).length) addBody.properties = line.properties;
      const addRes = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(addBody),
      });
      const addData = await addRes.json().catch(() => ({}));
      if (!addRes.ok || addData.status || addData.description) {
        try {
          await fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(addBody),
          });
        } catch (e) { /* swallow */ }
        return { swapped: false, reason: 're-add-failed' };
      }

      dispatchCartRefresh();
      return { swapped: true, newLineId: addData && addData.id, newPrice: sync.data && sync.data.price };
    } catch (e) {
      return { swapped: false, reason: 'exception', error: e && e.message };
    } finally {
      activeSwaps = Math.max(0, activeSwaps - 1);
    }
  }

  async function evaluateCart(cartData) {
    const gold = Kg.getGold();
    if (!gold) return { checked: 0, drifted: 0, swapped: 0 };

    const lines = collectCartLines();
    let drifted = 0;
    let swapped = 0;

    for (const { el, data } of lines) {
      if (!data.weight || !data.sku) continue;
      const computed = computeLinePrice(data, gold);
      if (!computed) continue;

      let storedUnitEgp = null;
      if (cartData && Array.isArray(cartData.items)) {
        const match = cartData.items.find((it) => String(it.sku || '').trim() === data.sku);
        if (match) {
          storedUnitEgp = Math.round(Number(match.original_price || match.price || 0) / 100);
          data.lineId = data.lineId || (Number(match.id) || 0);
          data.variantId = data.variantId || (Number(match.variant_id) || 0);
          data.quantity = data.quantity || (Number(match.quantity) || 1);
          if (match.properties && Object.keys(match.properties).length) {
            data.properties = match.properties;
          }
        }
      }
      if (storedUnitEgp == null) continue;

      if (Math.abs(storedUnitEgp - computed.unit) >= DRIFT_THRESHOLD_EGP) {
        drifted += 1;
        overrideLineDisplay(el, computed.line);
        const result = await swapLinePrice(data, computed.unit);
        if (result && result.swapped) swapped += 1;
      } else {
        overrideLineDisplay(el, computed.line);
      }
    }
    return { checked: lines.length, drifted, swapped };
  }

  async function evaluatePdpVariant() {
    if (!isProductPage()) return { refreshed: false };
    const el = document.querySelector('kg-live-price');
    const sku = el && el.dataset && el.dataset.initialSku ? el.dataset.initialSku.trim() : '';
    if (!sku) return { refreshed: false, reason: 'no-sku' };
    const last = Number(el.dataset.kgLastProxyAt || 0);
    if (Date.now() - last < POLL_MS - 30 * 1000) return { refreshed: false, reason: 'throttled-by-dom' };
    el.dataset.kgLastProxyAt = String(Date.now());
    const result = await Kg.syncVariantPrice(sku);
    return { refreshed: !!(result && result.ok), reason: result && result.reason };
  }

  async function runCycle() {
    if (!shouldRun()) return;
    if (inFlight) return;
    inFlight = true;
    try {
      const cartData = await fetchCart();
      const cartResult = await evaluateCart(cartData);
      const pdpResult = await evaluatePdpVariant();
      if (cartResult.drifted > 0 || pdpResult.refreshed) {
        console.log(`${LOG} cycle`, { cart: cartResult, pdp: pdpResult });
      }
    } catch (e) {
      console.warn(`${LOG} cycle error`, e);
    } finally {
      inFlight = false;
    }
  }

  function start() {
    if (pollTimer) return;
    runCycle();
    pollTimer = setInterval(runCycle, POLL_MS);
  }

  function onVisibility() {
    if (document.hidden) return;
    if (shouldRun()) runCycle();
  }

  function setCheckoutPending(button, pending) {
    if (!button) return;
    if (pending) {
      if (button.dataset.kgOrigLabel == null) {
        const span = button.querySelector('span');
        button._kgTextTarget = span || button;
        button.dataset.kgOrigLabel = (button._kgTextTarget.textContent || '').trim();
      }
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('disabled', 'disabled');
      button._kgTextTarget.textContent = 'Updating live prices…';
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('disabled');
      if (button.dataset.kgOrigLabel != null && button._kgTextTarget) {
        button._kgTextTarget.textContent = button.dataset.kgOrigLabel;
        delete button.dataset.kgOrigLabel;
        delete button._kgTextTarget;
      }
    }
  }

  function showCheckoutError(failures) {
    let host = document.getElementById('cart-errors');
    if (!host) {
      host = document.createElement('div');
      host.id = 'kg-checkout-error-host';
      host.style.cssText = 'margin:8px 0;padding:10px;background:#fde8e8;border:1px solid #f5b3b3;border-radius:6px;color:#8a1f1f;font-size:0.95rem;';
      const btn = document.querySelector('button[name="checkout"]');
      if (btn && btn.parentNode) {
        btn.parentNode.insertBefore(host, btn);
      } else {
        document.body.appendChild(host);
      }
    }
    const titles = failures.map((f) => f.title || f.sku).join(', ');
    const plural = failures.length > 1;
    const stockFailures = failures.filter((f) => f.reason === 're-add-failed');
    const priceFailures = failures.filter((f) => f.reason !== 're-add-failed');
    if (stockFailures.length > 0 && priceFailures.length === 0) {
      host.textContent = `Cannot proceed to checkout: ${titles} ${plural ? 'are' : 'is'} no longer in stock. Please remove ${plural ? 'them' : 'it'} and try again.`;
    } else {
      host.textContent = `Cannot proceed to checkout: could not confirm the live price for ${titles}. Please try again in a moment.`;
    }
    try { host.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* noop */ }
  }

  async function refreshAllCartLines(deadlineMs) {
    const gold = Kg.getGold();
    if (!gold) return { ok: true, failures: [], skipped: true };
    const cartData = await fetchCart();
    if (!cartData) return { ok: true, failures: [], skipped: true };

    const lines = collectCartLines();
    const failures = [];
    let swapped = 0;

    for (const { data } of lines) {
      if (Date.now() > deadlineMs) {
        // Ran out of time before checking every line — we don't know
        // whether the unevaluated ones have drifted, so fail closed
        // instead of letting an unverified line through to checkout.
        if (data.weight && data.sku) {
          failures.push({ sku: data.sku, title: data.sku, reason: 'timeout' });
        }
        continue;
      }
      if (!data.weight || !data.sku) continue;
      const match = cartData.items.find((it) => String(it.sku || '').trim() === data.sku);
      if (!match) continue;
      data.lineId = data.lineId || (Number(match.id) || 0);
      data.variantId = data.variantId || (Number(match.variant_id) || 0);
      data.quantity = data.quantity || (Number(match.quantity) || 1);
      if (match.properties && Object.keys(match.properties).length) {
        data.properties = match.properties;
      }

      const computed = computeLinePrice(data, gold);
      if (!computed) continue;
      const storedUnitEgp = Math.round(Number(match.original_price || match.price || 0) / 100);
      if (storedUnitEgp == null || Math.abs(storedUnitEgp - computed.unit) < DRIFT_THRESHOLD_EGP) continue;

      // A real price drift was detected — the line MUST end up swapped to
      // the live price before checkout proceeds. Any failure reason here
      // (not just re-add-failed) means we could not confirm the customer
      // is about to pay the live price, so it must block checkout too.
      const result = await swapLinePrice(data, computed.unit);
      if (result && result.swapped) {
        swapped += 1;
      } else {
        failures.push({ sku: data.sku, title: match.title || data.sku, reason: (result && result.reason) || 'unknown' });
      }
    }

    return { ok: failures.length === 0, failures, swapped, skipped: false };
  }

  async function onCheckoutSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const formId = form.id;
    if (formId !== 'cart' && formId !== 'CartDrawer-Form' && formId !== 'webicart') return;
    const submitter = event.submitter || document.activeElement;
    if (!submitter || !(submitter instanceof HTMLElement) || submitter.name !== 'checkout') return;

    if (form.dataset.kgCheckoutInFlight === '1') {
      delete form.dataset.kgCheckoutInFlight;
      return;
    }

    event.preventDefault();
    form.dataset.kgCheckoutInFlight = '1';
    const button = submitter;
    setCheckoutPending(button, true);

    const deadline = Date.now() + 8000;
    let result = { ok: true, failures: [] };
    try {
      result = await refreshAllCartLines(deadline);
    } catch (e) {
      console.warn(`${LOG} refresh error`, e);
    }

    if (result.failures && result.failures.length > 0) {
      showCheckoutError(result.failures);
      setCheckoutPending(button, false);
      delete form.dataset.kgCheckoutInFlight;
      return;
    }

    setCheckoutPending(button, false);
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(button);
    } else {
      form.submit();
    }
  }

  function installCheckoutIntercept() {
    if (!document.addEventListener) return;
    document.addEventListener('submit', onCheckoutSubmit, true);
  }

  function init() {
    if (isCheckoutPage()) return;
    installCheckoutIntercept();
    document.addEventListener('visibilitychange', onVisibility);
    start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
